/**
 * @file useVoteColor.ts
 * @description React hook for getting vote type colors for districts.
 */

import { useMemo } from 'react';
import { useDataStore, useVizStore } from '@/store';
import { VoteType, type DistrictId } from '@/types';

/**
 * Hook that returns a function to get the vote color for a district.
 *
 * This hook:
 * - Accesses district data from the data store
 * - Accesses vote color settings from the visualization store
 * - Returns a memoized function that maps district IDs to their vote type colors
 * - Uses fallback default colors if store colors are not set
 *
 * The returned function determines the color based on the district's vote type:
 * - YEA votes use `voteSupportColor` (default: '#80CFA9')
 * - NAY votes use `voteAgainstColor` (default: '#cccc1b')
 * - NO_VOTE uses `voteAbsenceColor` (default: '#EEEEEE')
 * - NO_DATA uses `voteNoDataColor` (default: '#CCCCCC')
 *
 * @returns Function that takes a district ID and returns the color string
 *          based on the district's vote type and store color settings.
 *
 * @example
 * ```tsx
 * const getVoteColor = useVoteColor();
 * const color = getVoteColor('118-ca-29');
 * ```
 */
export function useVoteColor(): (id: string) => string {
  const districts = useDataStore((s) => s.districts);
  const voteSupportColor = useVizStore((s) => s.voteSupportColor);
  const voteAgainstColor = useVizStore((s) => s.voteAgainstColor);
  const voteAbsenceColor = useVizStore((s) => s.voteAbsenceColor);
  const voteNoDataColor = useVizStore((s) => s.voteNoDataColor);

  return useMemo(() => {
    return (id: string): string => {
      const district = districts[id as DistrictId];
      const voteType = district?.voteType ?? VoteType.NO_DATA;

      if (voteType === VoteType.YEA) return voteSupportColor || '#80CFA9';
      if (voteType === VoteType.NAY) return voteAgainstColor || '#cccc1b';
      if (voteType === VoteType.NO_VOTE) return voteAbsenceColor || '#EEEEEE';
      return voteNoDataColor || '#CCCCCC';
    };
  }, [districts, voteSupportColor, voteAgainstColor, voteAbsenceColor, voteNoDataColor]);
}
